/**
 * saveImage — one-tap, mobile-first image saving.
 *
 * Strategy ladder:
 *   1. Web Share API with the image FILE attached (iOS/Android): opens the
 *      native share sheet where "Save Image" writes straight to Photos.
 *      This is the only genuinely good path in mobile Safari and wallet
 *      in-app browsers, where <a download> is ignored or broken.
 *   2. <a download> with an object URL (desktop browsers): a real download,
 *      no new tab.
 *   3. Open the image in a new tab (last resort, e.g. fetch blocked).
 *
 * A user cancelling the share sheet is treated as success (no fallback
 * popup) — they chose to close it.
 */
export type SaveOutcome = 'shared' | 'downloaded' | 'opened' | 'cancelled';

/**
 * True when the browser can hand an image FILE to the native share sheet
 * (the only web path that writes to Photos on iOS). Wallet in-app browsers
 * and some webviews can't — callers should surface the long-press hint.
 */
export function supportsFileShare(): boolean {
  const nav = navigator as Navigator & {
    canShare?: (data: { files: File[] }) => boolean;
  };
  if (typeof nav.canShare !== 'function') return false;
  try {
    const probe = new File([new Uint8Array(4)], 'probe.png', { type: 'image/png' });
    return nav.canShare({ files: [probe] });
  } catch {
    return false;
  }
}

/**
 * Re-encode an image blob as JPEG. Safari on recent iOS drops the
 * "Save Image" action from the share sheet for PNG files (WebKit
 * regression); sharing the same pixels as JPEG restores it.
 */
async function toJpeg(blob: Blob): Promise<Blob> {
  const bmp = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bmp.width;
  canvas.height = bmp.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no_canvas');
  // JPEG has no alpha — flatten onto white so transparency doesn't go black.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bmp, 0, 0);
  bmp.close();
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('encode_failed'))),
      'image/jpeg',
      0.92,
    );
  });
}

export async function saveImage(url: string, filename: string): Promise<SaveOutcome> {
  let blob: Blob;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`fetch_${res.status}`);
    blob = await res.blob();
  } catch {
    window.open(url, '_blank', 'noopener');
    return 'opened';
  }

  // iOS only offers "Save Image" (Photos) when the shared file is a real
  // image type — octet-stream blobs degrade the sheet to "Save to Files".
  const type = blob.type.startsWith('image/') ? blob.type : 'image/png';
  const nav = navigator as Navigator & {
    canShare?: (data: { files: File[] }) => boolean;
    share?: (data: { files: File[] }) => Promise<void>;
  };
  if (typeof nav.canShare === 'function') {
    // Share as JPEG: iOS's share sheet reliably offers "Save Image" for
    // JPEG but not PNG. Fall back to the original bytes if encoding fails.
    let shareBlob = blob;
    let shareName = filename;
    if (type === 'image/png') {
      try {
        shareBlob = await toJpeg(blob);
        shareName = filename.replace(/\.png$/i, '.jpg');
      } catch {
        shareBlob = blob;
      }
    }
    const file = new File([shareBlob], shareName, {
      type: shareBlob === blob ? type : 'image/jpeg',
    });
    if (nav.canShare({ files: [file] })) {
      try {
        await nav.share?.({ files: [file] });
        return 'shared';
      } catch (e) {
        if ((e as Error).name === 'AbortError') return 'cancelled';
        // fall through to download
      }
    }
  }

  try {
    const obj = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = obj;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(obj), 4000);
    return 'downloaded';
  } catch {
    window.open(url, '_blank', 'noopener');
    return 'opened';
  }
}
